import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { PenTool, Wrench, CheckCircle, Cog } from 'lucide-react'
import { PageHeader } from '@/components/sections/PageHeader'
import { CTASection } from '@/components/sections/CTASection'
import { IntegratedApproachSection } from '@/components/sections/IntegratedApproachSection'
import { PageMeta } from '@/components/PageMeta'

interface ProcessPhase {
  key: string
  number: string
  title: string
  subtitle: string
  items: { key: string; title: string; desc: string; image: string }[]
  icon: React.ComponentType<{ className?: string }>
  reverse?: boolean
}

function PhaseSection({ phase, index }: { phase: ProcessPhase; index: number }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const Icon = phase.icon

  return (
    <section
      ref={ref}
      className={`py-16 md:py-24 ${index % 2 === 1 ? 'bg-slate-50' : 'bg-white'}`}
    >
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* Phase Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 flex flex-col gap-8 md:mb-16 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="flex items-start gap-6">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-[#f75e47]/10 text-[#f75e47] lg:h-24 lg:w-24">
              <Icon className="h-10 w-10 lg:h-12 lg:w-12" />
            </div>
            <div>
              <span className="text-5xl font-bold text-slate-200 md:text-6xl lg:text-7xl">
                {phase.number}
              </span>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                {phase.title}
              </h2>
            </div>
          </div>
          <p className="max-w-xl text-lg leading-relaxed text-slate-600 lg:text-right">
            {phase.subtitle}
          </p>
        </motion.div>

        {/* Items Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {phase.items.map((item, idx) => (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-xl hover:shadow-[#f75e47]/10 hover:border-[#f75e47]/20 transition-all duration-300"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="text-xl font-semibold text-slate-900 group-hover:text-[#f75e47] transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
              {/* Decorative line */}
              <div className="h-1 w-0 bg-[#f75e47] transition-all duration-500 group-hover:w-full" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export function ProcessesPage() {
  const { t } = useTranslation('pages')

  const phases: ProcessPhase[] = [
    {
      key: 'design',
      number: '01',
      title: t('design.title'),
      subtitle: t('design.subtitle'),
      icon: PenTool,
      items: [
        {
          key: 'elettro',
          title: t('design.elettroTitle'),
          desc: t('design.elettroDesc'),
          image: '/media/images/PROGETTAZIONE-ELETTRICA80.jpg',
        },
        {
          key: 'meccanico',
          title: t('design.meccanicoTitle'),
          desc: t('design.meccanicoDesc'),
          image: '/media/images/PROGETTAZIONE-MECCANICA.jpg',
        },
        {
          key: 'software',
          title: t('design.softwareTitle'),
          desc: t('design.softwareDesc'),
          image: '/media/images/progettazione-software.png',
        },
      ],
    },
    {
      key: 'manufacturing',
      number: '02',
      title: t('manufacturing.title'),
      subtitle: t('manufacturing.subtitle'),
      icon: Wrench,
      items: [
        {
          key: 'officinaE',
          title: t('manufacturing.officinaETitle'),
          desc: t('manufacturing.officinaEDesc'),
          image: '/media/images/REALIZZAZIONE-OFFICINA-ELETTRICA-.jpg',
        },
        {
          key: 'officinaM',
          title: t('manufacturing.officinaMTitle'),
          desc: t('manufacturing.officinaMDesc'),
          image: '/media/images/REALIZZAZIONE-OFFICINA-MECCANICA.jpg',
        },
        {
          key: 'qualita',
          title: t('manufacturing.qualitaTitle'),
          desc: t('manufacturing.qualitaDesc'),
          image: '/media/images/fg-azienda.jpg',
        },
      ],
    },
    {
      key: 'finalStage',
      number: '03',
      title: t('finalStage.title'),
      subtitle: t('finalStage.subtitle'),
      icon: CheckCircle,
      items: [
        {
          key: 'install',
          title: t('finalStage.installTitle'),
          desc: t('finalStage.installDesc'),
          image: '/media/images/FINALE-INSTALLAZIONE.jpg',
        },
        {
          key: 'commissioning',
          title: t('finalStage.commissioningTitle'),
          desc: t('finalStage.commissioningDesc'),
          image: '/media/images/FINALE-COMMISIONING.jpg',
        },
        {
          key: 'remote',
          title: t('finalStage.remoteTitle'),
          desc: t('finalStage.remoteDesc'),
          image: '/media/images/FINALE-REMOTE-ASSITANCE.jpg',
        },
      ],
    },
  ]

  return (
    <>
      <PageMeta title={`${t('processes.title')} — FG Automazioni`} description={t('processes.subtitle')} />
      <PageHeader
        title={t('processes.title')}
        subtitle={t('processes.subtitle')}
        tag={t('processes.tag')}
        icon={Cog}
        gradient="purple"
        showCta={true}
        ctaText={t('processes.ctaButton')}
        ctaAction={() => document.getElementById('approccio')?.scrollIntoView({ behavior: 'smooth' })}
      />

      <div id="approccio">
        <IntegratedApproachSection />
      </div>

      {phases.map((phase, index) => (
        <PhaseSection key={phase.key} phase={phase} index={index} />
      ))}

      <CTASection />
    </>
  )
}
